import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Package, TrendingUp, BarChart3, DollarSign, Settings as SettingsIcon, LogOut, RefreshCw, Copy, Check, Menu, X, Phone, MessageCircle, Wallet as WalletIcon, Flame, Rocket, Lock, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import Overview from '../components/dashboard/Overview';
import Orders from '../components/dashboard/Orders';
import AdsTracking from '../components/dashboard/AdsTracking';
import Analytics from '../components/dashboard/Analytics';
import Payments from '../components/dashboard/Payments';
import Wallet from '../components/dashboard/Wallet';
import AdWallet from '../components/dashboard/AdWallet';
import Settings from '../components/dashboard/Settings';
import axios from 'axios';
import ActivationModal from '../components/ActivationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const [activationOpen, setActivationOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const fetchUserData = useCallback(async (principalId) => {
    try {
      const response = await axios.get(`${API}/users/${principalId}`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchUserData(parsedUser.principalId);
  }, [navigate, fetchUserData]);

  const fetchOnboarding = useCallback(async (principalId) => {
    if (!principalId) return;
    try {
      const { data } = await axios.get(`${API}/onboarding/${principalId}`);
      setOnboarding(data || null);
    } catch (_e) {
      setOnboarding(null);
    }
  }, []);

  const principalId = user?.principalId;
  useEffect(() => { if (principalId) fetchOnboarding(principalId); }, [principalId, fetchOnboarding, refreshKey]);

  // Access rules: ₹9 activation unlocks the dashboard; legacy paid clients
  // (activePlan / purchase completed) are treated as activated.
  const isActivated = !!(user && (user.isActivated || user.activePlan || onboarding?.purchaseStatus === 'Completed'));
  const onboardingDone = !!(onboarding?.startWorkStatus === 'Completed' || user?.activePlan);

  // Auto-navigate to the 4-step /enroll flow once per session for freshly-activated clients who haven't completed onboarding
  useEffect(() => {
    if (loading || !user || !isActivated || onboardingDone || !onboarding) return;
    const key = `enrollAutoShown_${user.principalId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    // Don't auto-navigate; let the banner prompt them instead (avoids unwanted redirects on refresh)
    // navigate('/enroll');
  }, [loading, user, isActivated, onboardingDone, onboarding]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  const handleRefresh = () => {
    if (user) {
      fetchUserData(user.principalId);
      setRefreshKey((k) => k + 1);
    }
  };

  const handleOnboardingDone = () => {
    if (user) fetchUserData(user.principalId);
    setRefreshKey((k) => k + 1);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedId(type);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Navigate to the new 4-step enrollment funnel (Package → Products → Shopify → Ads Budget → Razorpay)
  const openOnboarding = () => navigate('/enroll');

  const handleContactAgent = async () => {
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact-requests`, {
        userId: user.principalId,
        message: 'Contact request from dashboard'
      });
      toast({
        title: '✅ Request Submitted',
        description: 'Our agent will contact you shortly!',
      });
      setShowContactDialog(false);
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.response?.data?.detail || 'Failed to submit request',
        variant: 'destructive',
      });
    } finally {
      setContactLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', mobileLabel: 'Home', icon: Home },
    { id: 'orders', label: 'Orders', mobileLabel: 'Orders', icon: Package },
    { id: 'ads', label: 'Ads', mobileLabel: 'Ads', icon: TrendingUp },
    { id: 'adwallet', label: 'Ad Wallet', mobileLabel: 'AdFund', icon: Flame },
    { id: 'analytics', label: 'Analytics', mobileLabel: 'Stats', icon: BarChart3 },
    { id: 'payments', label: 'Payments', mobileLabel: 'Pay', icon: DollarSign },
    { id: 'wallet', label: 'Wallet', mobileLabel: 'Wallet', icon: WalletIcon },
    { id: 'settings', label: 'Settings', mobileLabel: 'More', icon: SettingsIcon },
  ];

  const setTabSafe = useCallback((tabId) => {
    if (!tabId) return;
    setActiveTab(tabId);
    // Ensure body pointer-events / scroll-lock are reset (in case a dialog left them disabled)
    document.body.style.pointerEvents = '';
    document.body.style.removeProperty('overflow');
    document.body.removeAttribute('data-scroll-locked');
    // Jump to top on tab change (window on mobile, <main> on desktop)
    window.scrollTo(0, 0);
    const m = document.getElementById('dashboard-main');
    if (m) m.scrollTop = 0;
  }, []);

  // Listen to QuickActions tab events + URL query params
  useEffect(() => {
    const handler = (e) => setTabSafe(e.detail);
    window.addEventListener('dashboard:set-tab', handler);
    // Read ?tab= from URL on mount
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl) setTabSafe(tabFromUrl);
    return () => window.removeEventListener('dashboard:set-tab', handler);
  }, [setTabSafe]);

  // Safety: if any earlier page left the body scroll-locked (e.g. an old Radix
  // Dialog), clear it once on mount so the dashboard is always scrollable/clickable.
  useEffect(() => {
    document.body.style.pointerEvents = 'auto';
    document.body.style.removeProperty('overflow');
    document.body.removeAttribute('data-scroll-locked');
    document.documentElement.style.removeProperty('overflow');
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview user={user} onRefresh={handleRefresh} onSetTab={setTabSafe} onOpenOnboarding={openOnboarding} refreshKey={refreshKey} />;
      case 'orders':
        return <Orders userId={user?.principalId} />;
      case 'ads':
        return <AdsTracking userId={user?.principalId} />;
      case 'adwallet':
        return <AdWallet userId={user?.principalId} user={user} />;
      case 'analytics':
        return <Analytics userId={user?.principalId} />;
      case 'payments':
        return <Payments userId={user?.principalId} />;
      case 'wallet':
        return <Wallet userId={user?.principalId} />;
      case 'settings':
        return <Settings userId={user?.principalId} user={user} />;
      default:
        return <Overview user={user} onRefresh={handleRefresh} onSetTab={setTabSafe} onOpenOnboarding={openOnboarding} refreshKey={refreshKey} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-800 text-xl">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (user && !isActivated) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col" data-testid="activation-gate">
        <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white/80 backdrop-blur border-b border-slate-200" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
          <button type="button" onClick={() => navigate('/')} className="flex items-center gap-2">
            <img src="/dropsquadz-logo.jpeg" alt="DropSquadz" className="w-9 h-9 rounded-full ring-2 ring-blue-500" />
            <span className="font-bold text-blue-600">DropSquadz</span>
          </button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600" data-testid="gate-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-6 text-white text-center">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3"><Lock className="w-7 h-7" /></div>
              <h1 className="text-xl sm:text-2xl font-extrabold">Activate Your Dashboard</h1>
              <p className="text-blue-50 text-sm mt-1">Hi {user?.name?.split(' ')[0] || 'there'}, unlock your DropSquadz dashboard with a one-time activation key.</p>
              <div className="mt-4 flex items-end justify-center gap-2">
                <span className="text-4xl font-extrabold">₹9</span>
                <span className="line-through text-blue-200 mb-1">₹499</span>
              </div>
            </div>
            <div className="p-5 sm:p-6 space-y-3">
              {['Instant dashboard access after payment', 'Complete onboarding — we start building your store', 'Winning products + Meta ads setup', 'Personal WhatsApp support'].map((t) => (
                <div key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> {t}</div>
              ))}
              <Button onClick={() => setActivationOpen(true)} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl mt-2" data-testid="gate-activate-btn">
                <Rocket className="w-5 h-5 mr-2" /> Pay ₹9 & Activate Now
              </Button>
              <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment via Cashfree</p>
            </div>
          </div>
        </div>
        <ActivationModal
          open={activationOpen}
          onClose={() => setActivationOpen(false)}
          activationFee={9}
          defaults={{ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' }}
          onActivated={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Contact Agent Modal (plain overlay) */}
      {showContactDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" aria-modal="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowContactDialog(false)} />
          <div className="relative bg-white border-2 border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full p-6">
            <button onClick={() => setShowContactDialog(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl text-center text-blue-600 flex items-center justify-center font-bold">
              <MessageCircle className="w-6 h-6 mr-2" />
              Contact Agent
            </h2>
            <div className="py-6 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-200">
                <Phone className="w-10 h-10 text-blue-600" />
              </div>
              <p className="text-slate-600 mb-6">
                Need help with anything? Our agent will contact you shortly.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleContactAgent}
                  disabled={contactLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg py-4 font-bold"
                >
                  {contactLoading ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Submitting...
                    </div>
                  ) : (
                    <>
                      <Phone className="w-5 h-5 mr-2" />
                      Request Callback
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowContactDialog(false)}
                  className="w-full hover:bg-slate-100 text-slate-600"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative flex flex-col lg:flex-row lg:h-screen">
        {/* Desktop Sidebar */}
        <aside className={`hidden lg:flex flex-col flex-shrink-0 bg-white border-r border-slate-200 shadow-lg transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
          {/* Logo Header */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img
                  src="/dropsquadz-logo.jpeg"
                  alt="DropSquadz"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500"
                />
                {sidebarOpen && (
                  <span className="text-lg font-bold text-blue-600">DropSquadz</span>
                )}
              </div>
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-slate-800">
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* User Info */}
          {sidebarOpen && (
            <div className="p-4 border-b border-slate-200">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500">Client ID</span>
                  <button onClick={() => copyToClipboard(user?.clientId?.toString(), 'client')} className="text-blue-600 hover:text-blue-700">
                    {copiedId === 'client' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
                <p className="font-mono font-bold text-blue-600">#{user?.clientId}</p>
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-xs text-slate-500 truncate">{user?.principalId}</p>
                </div>
                {!onboardingDone ? (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="inline-block px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs font-medium">
                      Onboarding Pending
                    </span>
                  </div>
                ) : user?.activePlan ? (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="inline-block px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium">
                      {user.activePlan} Plan
                    </span>
                  </div>
                ) : (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="inline-block px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs font-medium animate-pulse">
                      No Plan
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTabSafe(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all group ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : 'hover:bg-blue-50 text-slate-600'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'}`} />
                  {sidebarOpen && (
                    <span className={`font-medium ${activeTab === tab.id ? 'text-white' : 'text-slate-600 group-hover:text-blue-600'}`}>
                      {tab.label}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Contact Agent Button */}
          <div className="p-4 border-t border-slate-200">
            <Button
              onClick={() => setShowContactDialog(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
            >
              <Phone className="w-4 h-4 mr-2" />
              {sidebarOpen && 'Contact Agent'}
            </Button>
          </div>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-slate-200 space-y-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              className="w-full justify-start hover:bg-blue-50 text-slate-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {sidebarOpen && 'Refresh'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="w-full justify-start hover:bg-red-50 hover:text-red-600 text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {sidebarOpen && 'Logout'}
            </Button>
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200 safe-area-inset-top safe-area-inset-x">
          <div className="px-3 py-2.5 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img
                src="/dropsquadz-logo.jpeg"
                alt="DropSquadz"
                className="w-8 h-8 rounded-full ring-2 ring-blue-500"
              />
              <span className="font-bold text-blue-600 text-sm">#{user?.clientId}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-slate-600 px-2 h-9 min-h-0" aria-label="Refresh">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 px-2 h-9 min-h-0" aria-label="Logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main
          id="dashboard-main"
          className="flex-1 min-w-0 lg:overflow-y-auto px-3 sm:px-4 lg:p-6 pt-[72px] sm:pt-20 lg:pt-6 pb-28 lg:pb-6 bg-slate-50"
          data-testid="dashboard-main"
        >
          {!onboardingDone && (
            <div className="mb-4 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 shadow-lg flex flex-col sm:flex-row sm:items-center gap-3" data-testid="onboarding-required-banner">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0"><Rocket className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <p className="font-bold text-sm sm:text-base">Complete onboarding to start work</p>
                  <p className="text-xs sm:text-sm text-orange-50">Pick your plan, products, Shopify & ads budget — our team starts building your store right after.</p>
                </div>
              </div>
              <Button onClick={openOnboarding} className="bg-white text-orange-600 hover:bg-orange-50 font-bold rounded-xl w-full sm:w-auto flex-shrink-0" data-testid="banner-start-onboarding-btn">
                Start Onboarding <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
          {renderContent()}
        </main>

        {/* Mobile Bottom Navigation — dynamic & cool: elevated active icon + animated indicator */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-2xl border-t border-slate-200 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.12)] safe-area-inset-bottom" data-testid="mobile-bottom-nav">
          <div className="no-mobile-pad grid grid-cols-8 items-end px-1.5 pt-1.5 pb-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTabSafe(tab.id)}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                  data-testid={`mobile-tab-${tab.id}`}
                  className="group relative min-w-0 flex flex-col items-center justify-end pb-0.5 active:scale-90 transition-transform duration-200"
                >
                  {/* animated top indicator */}
                  <span
                    className={`absolute top-0 h-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300 ${isActive ? 'w-6 opacity-100' : 'w-0 opacity-0'}`}
                  />
                  {/* icon bubble — elevated when active */}
                  <span
                    className={`flex items-center justify-center rounded-2xl transition-all duration-300 ${
                      isActive
                        ? '-translate-y-2 w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg shadow-blue-500/40 ring-2 ring-white'
                        : 'translate-y-0 w-9 h-9 bg-transparent'
                    }`}
                  >
                    <Icon className={`transition-all duration-300 ${isActive ? 'w-5 h-5 text-white' : 'w-5 h-5 text-slate-400 group-hover:text-blue-500'}`} />
                  </span>
                  <span
                    className={`text-[9px] leading-tight whitespace-nowrap truncate w-full text-center tracking-tight transition-all duration-300 ${
                      isActive ? 'text-blue-600 font-bold -mt-1' : 'text-slate-400 font-medium mt-0.5'
                    }`}
                  >
                    {tab.mobileLabel || tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Dashboard;
